import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Modal from '@mui/material/Modal';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import PeopleIcon from '@mui/icons-material/People';
import UnsubscribeIcon from '@mui/icons-material/Unsubscribe';
import HowToRegIcon from '@mui/icons-material/HowToReg';

import {
    adminGetCampaigns,
    adminAddCampaign,
    adminGetCampaign,
    adminUpdateCampaign,
    adminDeleteCampaign,
    adminPreviewCampaign,
    adminTestCampaign,
    adminSendCampaign,
    adminCancelCampaign,
    adminGetSuppressions,
    adminAddSuppression,
    adminDeleteSuppression,
    CampaignDraft,
} from '../../api/Api';
import { useColors } from '../../theme/ColorModeContext';
import PageContainer from '../../shared/PageContainer';
import GlowCard from '../../shared/GlowCard';
import CyberpunkTitle from '../../shared/CyberpunkTitle';
import StatCard from '../../shared/StatCard';

interface PropsAdminEmails {
    token: string;
}

interface Campaign {
    id: string;
    subject: string;
    preheader: string | null;
    body_markdown: string;
    status: string;
    created_at: string;
    recipient_count: number;
    sent_count: number;
    failed_count: number;
    skipped_count: number;
    error: string | null;
}

interface AudienceStats {
    total_users: number;
    confirmed_users: number;
    subscribed: number;
    unsubscribed: number;
    deliverable_audience: number;
}

interface Suppression {
    id: string;
    email: string;
    reason: string;
    note: string | null;
    created_at: string;
}

const EMPTY_DRAFT: CampaignDraft = { subject: '', preheader: '', body_markdown: '' };

const STATUS_COLORS: Record<string, 'accent' | 'muted' | 'green' | 'red'> = {
    draft: 'muted',
    queued: 'accent',
    sending: 'accent',
    sent: 'green',
    canceled: 'muted',
    failed: 'red',
};

const AdminEmails = (props: PropsAdminEmails) => {
    const colors = useColors();
    const token = props.token;

    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [audience, setAudience] = useState<AudienceStats | null>(null);
    const [suppressions, setSuppressions] = useState<Suppression[]>([]);

    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [draft, setDraft] = useState<CampaignDraft>(EMPTY_DRAFT);
    const [selected, setSelected] = useState<Campaign | null>(null);
    const [message, setMessage] = useState('');
    const [busy, setBusy] = useState(false);

    const [previewHtml, setPreviewHtml] = useState('');
    const [sendOpen, setSendOpen] = useState(false);
    const [sendConfirmation, setSendConfirmation] = useState('');

    const [suppressionEmail, setSuppressionEmail] = useState('');
    const [suppressionReason, setSuppressionReason] = useState('bounce');

    const statusColor = (status: string) => {
        const key = STATUS_COLORS[status] || 'muted';
        if (key === 'green') return colors.accent.green;
        if (key === 'red') return colors.accent.red;
        if (key === 'accent') return colors.accent.cyan;
        return colors.text.muted;
    };

    const refresh = useCallback(async () => {
        const data = await adminGetCampaigns(token);
        if (data) {
            setCampaigns(data.campaigns);
            setAudience(data.audience);
        }
        const supp = await adminGetSuppressions(token);
        if (supp) setSuppressions(supp.suppressions);
    }, [token]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const loadCampaign = async (campaignId: string) => {
        const data = await adminGetCampaign(token, campaignId);
        if (!data) return;
        setSelectedId(campaignId);
        setSelected(data);
        setDraft({
            subject: data.subject,
            preheader: data.preheader || '',
            body_markdown: data.body_markdown,
        });
        setPreviewHtml('');
        setMessage('');
    };

    const startNew = () => {
        setSelectedId(null);
        setSelected(null);
        setDraft(EMPTY_DRAFT);
        setPreviewHtml('');
        setMessage('');
    };

    const handleSave = async () => {
        setBusy(true);
        const result = selectedId
            ? await adminUpdateCampaign(token, selectedId, draft)
            : await adminAddCampaign(token, draft);
        setBusy(false);

        if (!result) { setMessage('Save failed'); return; }
        if (result.msg) { setMessage(result.msg); return; }

        setMessage('Saved');
        setSelectedId(result.id);
        setSelected(result);
        await refresh();
    };

    const handlePreview = async () => {
        if (!selectedId) { setMessage('Save the draft first'); return; }
        setBusy(true);
        const result = await adminPreviewCampaign(token, selectedId);
        setBusy(false);
        if (!result) { setMessage('Preview failed'); return; }
        setPreviewHtml(result.html);
    };

    const handleTest = async () => {
        if (!selectedId) { setMessage('Save the draft first'); return; }
        setBusy(true);
        const result = await adminTestCampaign(token, selectedId);
        setBusy(false);
        setMessage(result?.msg || 'Test send failed');
    };

    const handleSend = async () => {
        if (!selectedId || !audience) return;
        setBusy(true);
        const result = await adminSendCampaign(token, selectedId, audience.deliverable_audience);
        setBusy(false);
        setSendOpen(false);
        setSendConfirmation('');
        setMessage(result?.msg || 'Send failed');
        await refresh();
        if (selectedId) await loadCampaign(selectedId);
    };

    const handleCancel = async () => {
        if (!selectedId) return;
        setBusy(true);
        const result = await adminCancelCampaign(token, selectedId);
        setBusy(false);
        setMessage(result?.msg || 'Cancel failed');
        await refresh();
        await loadCampaign(selectedId);
    };

    const handleDelete = async () => {
        if (!selectedId) return;
        setBusy(true);
        const result = await adminDeleteCampaign(token, selectedId);
        setBusy(false);
        setMessage(result?.msg || 'Delete failed');
        startNew();
        await refresh();
    };

    const handleAddSuppression = async () => {
        if (!suppressionEmail) return;
        setBusy(true);
        await adminAddSuppression(token, suppressionEmail, suppressionReason);
        setBusy(false);
        setSuppressionEmail('');
        await refresh();
    };

    const handleRemoveSuppression = async (suppressionId: string) => {
        await adminDeleteSuppression(token, suppressionId);
        await refresh();
    };

    const isDraft = !selected || selected.status === 'draft';
    const canCancel = selected && (selected.status === 'queued' || selected.status === 'sending');
    const expectedConfirmation = audience ? String(audience.deliverable_audience) : '';

    return (
        <PageContainer>
            <CyberpunkTitle
                title="Campaigns"
                subtitle={audience ? `${audience.deliverable_audience} OPTED IN` : 'LOADING'}
                mb="24px"
            />

            {audience && (
                <Grid container spacing={2} sx={{ marginBottom: '24px' }}>
                    <Grid item xs={6} md={3}>
                        <StatCard
                            icon={<MarkEmailReadIcon />}
                            label="Deliverable"
                            value={audience.deliverable_audience}
                        />
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <StatCard
                            icon={<PeopleIcon />}
                            label="Subscribed"
                            value={audience.subscribed}
                        />
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <StatCard
                            icon={<UnsubscribeIcon />}
                            label="Unsubscribed"
                            value={audience.unsubscribed}
                            accentColor={colors.text.muted}
                        />
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <StatCard
                            icon={<HowToRegIcon />}
                            label="Confirmed users"
                            value={audience.confirmed_users}
                        />
                    </Grid>
                </Grid>
            )}

            {audience && audience.deliverable_audience < audience.subscribed && (
                <Typography variant="body2" sx={{ color: colors.text.muted, marginBottom: '16px' }}>
                    {audience.subscribed - audience.deliverable_audience} subscriber(s) are excluded
                    because their address is unconfirmed or suppressed.
                </Typography>
            )}

            <Grid container spacing={3}>
                {/* Campaign list */}
                <Grid item xs={12} md={5}>
                    <GlowCard glowOnHover={false} sx={{ padding: '20px' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                            <Typography variant="h6" sx={{ fontFamily: '"Orbitron", sans-serif' }}>
                                Campaigns
                            </Typography>
                            <Button size="small" variant="outlined" onClick={startNew}>New</Button>
                        </Box>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ color: colors.accent.cyan }}>Subject</TableCell>
                                        <TableCell sx={{ color: colors.accent.cyan }}>Status</TableCell>
                                        <TableCell sx={{ color: colors.accent.cyan }} align="right">Sent</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {campaigns.map((campaign) => (
                                        <TableRow
                                            key={campaign.id}
                                            hover
                                            onClick={() => loadCampaign(campaign.id)}
                                            sx={{
                                                cursor: 'pointer',
                                                backgroundColor: campaign.id === selectedId
                                                    ? colors.accent.cyanGlow : 'transparent',
                                            }}
                                        >
                                            <TableCell sx={{ color: colors.text.primary }}>{campaign.subject}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={campaign.status}
                                                    size="small"
                                                    sx={{
                                                        color: statusColor(campaign.status),
                                                        borderColor: statusColor(campaign.status),
                                                    }}
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell align="right" sx={{ color: colors.text.secondary }}>
                                                {campaign.sent_count}/{campaign.recipient_count}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {campaigns.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={3} sx={{ color: colors.text.muted }}>
                                                No campaigns yet.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </GlowCard>
                </Grid>

                {/* Composer */}
                <Grid item xs={12} md={7}>
                    <GlowCard glowOnHover={false} sx={{ padding: '24px' }}>
                        <Typography variant="h6" sx={{ fontFamily: '"Orbitron", sans-serif', marginBottom: '12px' }}>
                            {selectedId ? 'Edit campaign' : 'New campaign'}
                        </Typography>

                        {selected && !isDraft && (
                            <Typography variant="body2" sx={{ color: colors.text.muted, marginBottom: '12px' }}>
                                This campaign is {selected.status} and can no longer be edited.
                                {selected.error && ` Error: ${selected.error}`}
                            </Typography>
                        )}

                        <TextField
                            fullWidth margin="normal" label="Subject"
                            value={draft.subject} disabled={!isDraft}
                            onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
                        />
                        <TextField
                            fullWidth margin="normal" label="Preheader (inbox preview text)"
                            value={draft.preheader} disabled={!isDraft}
                            onChange={(e) => setDraft({ ...draft, preheader: e.target.value })}
                        />
                        <TextField
                            fullWidth margin="normal" label="Body (markdown)"
                            multiline minRows={12}
                            value={draft.body_markdown} disabled={!isDraft}
                            onChange={(e) => setDraft({ ...draft, body_markdown: e.target.value })}
                            helperText="Headings, lists, **bold**, *italic*, `code` and [links](https://...). The unsubscribe footer is added automatically."
                        />

                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '16px' }}>
                            {isDraft && (
                                <Button variant="contained" onClick={handleSave} disabled={busy}>Save</Button>
                            )}
                            <Button variant="outlined" onClick={handlePreview} disabled={busy || !selectedId}>
                                Preview
                            </Button>
                            <Button variant="outlined" onClick={handleTest} disabled={busy || !selectedId}>
                                Send test to me
                            </Button>
                            {isDraft && selectedId && (
                                <Button
                                    variant="contained"
                                    onClick={() => setSendOpen(true)}
                                    disabled={busy || !audience || audience.deliverable_audience === 0}
                                >
                                    Send to {audience?.deliverable_audience ?? 0}
                                </Button>
                            )}
                            {canCancel && (
                                <Button variant="outlined" color="error" onClick={handleCancel} disabled={busy}>
                                    Cancel send
                                </Button>
                            )}
                            {selectedId && selected?.status !== 'sending' && (
                                <Button variant="outlined" color="error" onClick={handleDelete} disabled={busy}>
                                    Delete
                                </Button>
                            )}
                            {busy && <CircularProgress size={22} sx={{ alignSelf: 'center' }} />}
                        </Box>

                        {message && (
                            <Typography variant="body2" sx={{
                                color: colors.accent.cyan,
                                marginTop: '14px',
                                padding: '8px 12px',
                                backgroundColor: colors.accent.cyanGlow,
                                borderRadius: '8px',
                            }}>
                                {message}
                            </Typography>
                        )}

                        {selected && selected.recipient_count > 0 && (
                            <Typography variant="body2" sx={{ color: colors.text.secondary, marginTop: '14px' }}>
                                {selected.sent_count} sent · {selected.skipped_count} skipped ·{' '}
                                {selected.failed_count} failed of {selected.recipient_count} queued
                            </Typography>
                        )}
                    </GlowCard>

                    {previewHtml && (
                        <GlowCard glowOnHover={false} sx={{ padding: '16px', marginTop: '20px' }}>
                            <Typography variant="h6" sx={{ fontFamily: '"Orbitron", sans-serif', marginBottom: '12px' }}>
                                Preview
                            </Typography>
                            {/*
                              * Sandboxed iframe: the rendered email is displayed exactly as a
                              * mail client would show it, without its styles leaking into the
                              * admin panel or its markup running here.
                              */}
                            <iframe
                                title="Campaign preview"
                                srcDoc={previewHtml}
                                sandbox=""
                                style={{ width: '100%', height: '520px', border: 'none', borderRadius: '8px', background: '#fff' }}
                            />
                        </GlowCard>
                    )}
                </Grid>
            </Grid>

            {/* Suppression list */}
            <Typography variant="h5" sx={{ fontFamily: '"Orbitron", sans-serif', margin: '36px 0 16px' }}>
                Suppressed addresses
            </Typography>
            <GlowCard glowOnHover={false} sx={{ padding: '20px' }}>
                <Typography variant="body2" sx={{ color: colors.text.secondary, marginBottom: '16px' }}>
                    Addresses here never receive marketing, whatever their consent says. Add hard
                    bounces and spam complaints; repeatedly mailing them is what damages the
                    sending domain that password resets also depend on.
                </Typography>
                <Box sx={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px' }}>
                    <TextField
                        size="small" label="Email" value={suppressionEmail}
                        onChange={(e) => setSuppressionEmail(e.target.value)}
                        sx={{ minWidth: '260px' }}
                    />
                    <TextField
                        size="small" select label="Reason" value={suppressionReason}
                        onChange={(e) => setSuppressionReason(e.target.value)}
                        sx={{ minWidth: '150px' }}
                    >
                        <MenuItem value="bounce">Bounce</MenuItem>
                        <MenuItem value="complaint">Complaint</MenuItem>
                        <MenuItem value="manual">Manual</MenuItem>
                    </TextField>
                    <Button variant="outlined" onClick={handleAddSuppression} disabled={busy}>Add</Button>
                </Box>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ color: colors.accent.cyan }}>Email</TableCell>
                                <TableCell sx={{ color: colors.accent.cyan }}>Reason</TableCell>
                                <TableCell sx={{ color: colors.accent.cyan }} align="right"></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {suppressions.map((row) => (
                                <TableRow key={row.id}>
                                    <TableCell sx={{ color: colors.text.primary }}>{row.email}</TableCell>
                                    <TableCell sx={{ color: colors.text.secondary }}>{row.reason}</TableCell>
                                    <TableCell align="right">
                                        <Button size="small" onClick={() => handleRemoveSuppression(row.id)}>
                                            Remove
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {suppressions.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={3} sx={{ color: colors.text.muted }}>
                                        Nothing suppressed.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </GlowCard>

            {/* Send confirmation: typing the count makes the size of the send
                impossible to miss, and the API re-checks it server-side. */}
            <Modal open={sendOpen} onClose={() => setSendOpen(false)}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '520px',
                    maxWidth: '90vw',
                    backgroundColor: colors.bg.secondary,
                    border: `1px solid ${colors.border.default}`,
                    borderRadius: '8px',
                    overflow: 'hidden',
                }}>
                    <Typography variant="h6" sx={{
                        padding: '18px 24px',
                        fontFamily: '"Orbitron", sans-serif',
                        borderBottom: `1px solid ${colors.border.subtle}`,
                        backgroundColor: colors.bg.tertiary,
                    }}>
                        Send campaign
                    </Typography>
                    <Box sx={{ padding: '24px' }}>
                        <Typography variant="body2" sx={{ color: colors.text.secondary, marginBottom: '8px' }}>
                            This queues <strong>{draft.subject}</strong> for{' '}
                            <strong>{expectedConfirmation}</strong> opted-in recipients. It cannot be undone
                            for messages already delivered.
                        </Typography>
                        <Typography variant="body2" sx={{ color: colors.text.muted, marginBottom: '16px' }}>
                            Send yourself a test first if you have not already.
                        </Typography>
                        <TextField
                            fullWidth size="small"
                            label={`Type ${expectedConfirmation} to confirm`}
                            value={sendConfirmation}
                            onChange={(e) => setSendConfirmation(e.target.value)}
                        />
                        <Grid container spacing={2} sx={{ marginTop: '8px' }}>
                            <Grid item xs={6}>
                                <Button fullWidth variant="outlined" onClick={() => setSendOpen(false)}>
                                    Cancel
                                </Button>
                            </Grid>
                            <Grid item xs={6}>
                                <Button
                                    fullWidth variant="contained"
                                    disabled={busy || sendConfirmation !== expectedConfirmation}
                                    onClick={handleSend}
                                >
                                    Send now
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>
                </Box>
            </Modal>
        </PageContainer>
    );
};

export default AdminEmails;
